import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Site {
  id: string;
  location_name: string;
  site_code: string | null;
}

interface SiteFilterProps {
  value: string;
  onChange: (siteId: string) => void;
  className?: string;
  showAllOption?: boolean;
  allLabel?: string;
}

export const SiteFilter = ({ 
  value, 
  onChange, 
  className = "w-[200px]",
  showAllOption = true,
  allLabel = "All Sites"
}: SiteFilterProps) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('company_locations')
        .select('id, location_name, site_code')
        .eq('is_active', true)
        .order('location_name', { ascending: true });

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Select value={value} onValueChange={onChange} disabled={loading}>
      <SelectTrigger className={className}>
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <SelectValue placeholder={loading ? "Loading..." : "Select site"} />
        </div>
      </SelectTrigger>
      <SelectContent>
        {showAllOption && (
          <SelectItem value="all">{allLabel}</SelectItem>
        )}
        {sites.map((site) => (
          <SelectItem key={site.id} value={site.id}>
            {site.location_name} {site.site_code ? `(${site.site_code})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default SiteFilter;
